"use strict";
exports.__esModule = true;
var checksum_1 = require("@iota/checksum");
var Promise = require("bluebird");
var errors = require("../../errors");
var guards_1 = require("../../guards");
var types_1 = require("../../types");
var _1 = require("./");
var createWereAddressesSpentFrom_1 = require("./createWereAddressesSpentFrom");
exports.createIsAddressUsed = function (provider) {
    var wereAddressesSpentFrom = createWereAddressesSpentFrom_1.createWereAddressesSpentFrom(provider, 'lib');
    var findTransactions = _1.createFindTransactions(provider);
    return function (address) {
        return wereAddressesSpentFrom([address]).then(function (_a) {
            var isSpent = _a[0];
            return findTransactions({ addresses: [address] }).then(function (transactions) { return ({
                isUsed: isSpent || transactions.length > 0,
                isSpent: isSpent,
                transactions: transactions
            }); });
        });
    };
};
/**
 * Generates and returns all addresses up to the first unused addresses including it.
 *
 * @method getUntilFirstUnusedAddress
 *
 * @ignore
 *
 * @memberof module:core
 *
 * @param {string} seed
 * @param {options} [options]
 * @param {number} [options.start=0] - Key index offset to start the search at
 * @param {number} [options.security=2] - Security level
 *
 * @return {Promise}
 * @fulfil {Hash[]} List of addresses up to (and including) first unused address
 * @reject {Error}
 * - `INVALID_SEED`
 * - `INVALID_START_OPTION`
 * - `INVALID_SECURITY`
 * - Fetch error
 */
exports.getUntilFirstUnusedAddress = function (isAddressUsed, seed, index, security, returnAll) {
    var addressList = [];
    var iterate = function () {
        var nextAddress = _1.generateAddress(seed, index++, security);
        if (returnAll) {
            addressList.push(nextAddress);
        }
        return isAddressUsed(nextAddress).then(function (_a) {
            var isUsed = _a.isUsed;
            if (isUsed) {
                return iterate();
            }
            // It may have already been added
            if (!returnAll) {
                addressList.push(nextAddress);
            }
            return addressList;
        });
    };
    return iterate;
};
exports.generateAddresses = function (seed, index, security, total) {
    if (total === void 0) { total = 1; }
    return Array(total)
        .fill('')
        .map(function () { return _1.generateAddress(seed, index++, security); });
};
exports.applyChecksumOption = function (checksum) { return function (addresses) {
    return checksum
        ? Array.isArray(addresses)
            ? addresses.map(function (addr) { return checksum_1.addChecksum(addr); })
            : checksum_1.addChecksum(addresses)
        : addresses;
}; };
exports.applyReturnAllOption = function (returnAll, total) { return function (addresses) {
    return returnAll || total ? addresses : addresses[addresses.length - 1];
}; };
exports.getNewAddressOptions = types_1.getOptionsWithDefaults({
    index: 0,
    security: 2,
    checksum: false,
    total: undefined,
    returnAll: false
});
/**
 * @method createGetNewAddress
 *
 * @param {Provider} provider - Network provider
 *
 * @memberof module:core
 *
 * @return {function} {@link #module_core.getNewAddress `getNewAddress`}
 */
exports.createGetNewAddress = function (provider, caller) {
    var isAddressUsed = exports.createIsAddressUsed(provider);
    /**
     * Generates and returns a new address by calling [`findTransactions`]{@link #module_core.findTransactions}
     * until the first unused address is detected. This stops working after a snapshot.
     *
     * @example
     * ```js
     * getNewAddress(seed, { index })
     *   .then(address => {
     *     // ...
     *   })
     *   .catch(err => {
     *     // ...
     *   })
     * ```
     *
     * @method getNewAddress
     *
     * @memberof module:core
     *
     * @param {string} seed - At least 81 trytes long seed
     * @param {object} [options]
     * @param {number} [options.index=0] - Key index to start search at
     * @param {number} [options.security=2] - Security level
     * @param {boolean} [options.checksum=false] - `Deprecated` Flag to include 9-trytes checksum or not
     * @param {number} [options.total] - `Deprecated` Number of addresses to generate.
     * @param {boolean} [options.returnAll=false] - `Deprecated` Flag to return all addresses, from start up to new address.
     * @param {Callback} [callback] - Optional callback
     *
     * @return {Promise}
     * @fulfil {Hash|Hash[]} New (unused) address or list of addresses up to (and including) first unused address
     * @reject {Error}
     * - `INVALID_SEED`
     * - `INVALID_START_OPTION`
     * - `INVALID_SECURITY`
     * - Fetch error
     */
    return function getNewAddress(seed, options, callback) {
        if (options === void 0) { options = {}; }
        if (caller !== 'lib') {
            var deprecated = [];
            if (options.total !== undefined) {
                deprecated.push(options.total);
            }
            if (options.returnAll !== undefined) {
                deprecated.push(options.returnAll);
            }
            if (options.checksum !== undefined) {
                deprecated.push(options.checksum);
            }
            /* tslint:disable-next-line:no-console */
            console.warn("`GetNewAddressOptions`: " + deprecated.join(',') + " options are deprecated and will be removed in v.2.0.0. \n");
        }
        var _a = exports.getNewAddressOptions(options), index = _a.index, security = _a.security, total = _a.total, returnAll = _a.returnAll, checksum = _a.checksum;
        return Promise.resolve(guards_1.validate(guards_1.seedValidator(seed), guards_1.indexValidator(index), guards_1.securityLevelValidator(security), (!!total || total === 0) && [total, function (t) { return Number.isInteger(t) && t > 0; }, errors.INVALID_TOTAL_OPTION]))
            .then(function () {
            return total && total > 0
                ? exports.generateAddresses(seed, index, security, total)
                : Promise["try"](exports.getUntilFirstUnusedAddress(isAddressUsed, seed, index, security, returnAll));
        })
            .then(exports.applyReturnAllOption(returnAll, total))
            .then(exports.applyChecksumOption(checksum))
            .asCallback(callback);
    };
};
//# sourceMappingURL=createGetNewAddress.js.map